<script setup lang="ts">
import { computed, type ComputedRef, nextTick, type Ref, ref } from 'vue';
import gettext from '@/i18n/gettext';
import { INFO_ROBCO_BASE_URL } from '@/Constants';

const { $gettext } = gettext;

interface Video {
  name: string;
  description: string;
  url: string;
}

interface VideoCategory {
  name: string;
  videos: Video[];
}

const videoCategories: ComputedRef<VideoCategory[]> = computed(() => [
  {
    name: $gettext('RobFlow Basics'),
    videos: [
      {
        name: $gettext('Intro'),
        description: $gettext('Learn the foundations of RobFlow'),
        url: `${INFO_ROBCO_BASE_URL}/hubfs/RobFlow%20Tutorials/RobFlow%20Tutorials-%20Intro.mp4`,
      },
    ],
  },
  {
    name: $gettext('Flows'),
    videos: [
      {
        name: $gettext('Palletizing'),
        description: $gettext('Learn about the palletizing solution'),
        url: `${INFO_ROBCO_BASE_URL}/hubfs/RobFlow%20Tutorials/RobFlow%20Tutorials-%20Paletizing.mp4`,
      },
    ],
  },
  {
    name: $gettext('Dashboards'),
    videos: [
      {
        name: $gettext('Dashboards Basics'),
        description: $gettext(
          'Learn to build Dashboards that are applicable to you',
        ),
        url: `${INFO_ROBCO_BASE_URL}/hubfs/RobFlow%20Tutorials/RobFlow%20Tutorials-%20Dashboard%20Basics%20(1).mp4`,
      },
    ],
  },
  {
    name: $gettext('Variables'),
    videos: [
      {
        name: $gettext('Variables Basics'),
        description: $gettext(
          'Learn to improve your efficiency by using variables',
        ),
        url: `${INFO_ROBCO_BASE_URL}/hubfs/RobFlow%20Tutorials/RobFlow%20Tutorials-%20Variables%20Basics.mp4`,
      },
    ],
  },
]);

const selectedVideo: Ref<Video> = ref(videoCategories.value[0].videos[0]);
const dialogOpen = ref(false);
function openVideo(video: Video) {
  selectedVideo.value = video;
  dialogOpen.value = false;
  nextTick(() => {
    dialogOpen.value = true;
  });
}
</script>

<template>
  <div class="pa-4 pa-sm-12">
    <v-row>
      <v-col cols="12" md="4">
        <v-row style="max-width: 400px">
          <v-col cols="12">
            <h1 style="margin-bottom: 72px">
              {{ $gettext('Tutorials') }}
            </h1>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-expansion-panels>
        <v-col
          v-for="(category, idx) in videoCategories"
          v-bind:key="idx"
          cols="12"
        >
          <v-expansion-panel class="tutorial-category">
            <template #title>
              <h3 class="tutorial-category__title">{{ category.name }}</h3>
              <v-spacer />
            </template>
            <template #text>
              <v-row class="mt-1">
                <v-col
                  v-for="video in category.videos"
                  :key="video.name"
                  cols="12"
                  md="6"
                  lg="4"
                  xl="3"
                >
                  <v-card style="height: 100%" @click="openVideo(video)">
                    <div class="d-flex justify-center align-center flex-column">
                      <div class="text-h5 mt-4 text-center">
                        {{ video.name }}
                      </div>
                      <v-icon
                        size="x-large"
                        icon="mdi-play-circle-outline"
                        class="justify-center align-center mt-2"
                      />
                      <div class="mt-5 ma-3 text-center">
                        {{ video.description }}
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel>
        </v-col>
      </v-expansion-panels>
    </v-row>
    <v-dialog :model-value="dialogOpen">
      <v-card>
        <v-card-title class="ml-3 mt-3">{{ selectedVideo.name }}</v-card-title>
        <v-card-text style="height: 80vh">
          <div class="video-container">
            <iframe
              :title="selectedVideo.name"
              :src="selectedVideo.url"
              allow="autoplay; fullscreen"
              allowfullscreen
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="dialogOpen = false">{{ $gettext('Close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<style lang="scss">
.tutorial-category {
  &__title {
  }
}
.video-container {
  position: relative;
  height: 100%;

  iframe {
    border-radius: 16px;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
